import {devLog} from './utils';

interface MessageHandler<TPayload = unknown, TResponse = void> {
  (payload: TPayload): TResponse | Promise<TResponse>;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
}

const generateRequestId = (messageType: string): string =>
  `req_${String(messageType)}_${Date.now()}`;

const MESSENGER_TYPE = 'iframe-messenger';

export class Messenger<
  TRegistry extends Record<
    string,
    {payload: unknown; response: unknown}
  > = Record<string, {payload: unknown; response: unknown}>,
> {
  private targetWindow: Window;

  private targetOrigin: string;

  private messageHandlers = new Map<
    keyof TRegistry,
    MessageHandler<unknown, unknown>
  >();

  private pendingRequests = new Map<string, PendingRequest>();

  private boundMessageListener: (event: MessageEvent) => void;

  constructor(targetWindow: Window, targetOrigin = '*') {
    this.targetWindow = targetWindow;
    this.targetOrigin = targetOrigin;
    this.boundMessageListener = this.handleMessage.bind(this);
    this.setupMessageListener();
  }

  private setupMessageListener(): void {
    devLog('[Messenger] setting up message listener');
    window.addEventListener('message', this.boundMessageListener);
  }

  private handleMessage(event: MessageEvent): void {
    // Validate origin if specified
    if (this.targetOrigin !== '*' && event.origin !== this.targetOrigin) {
      return;
    }

    const {messengerType, method, payload, requestId, response, error} =
      event.data;

    // Only process messages from this messenger type
    if (messengerType !== MESSENGER_TYPE) {
      return;
    }

    // Handle response to a previous request
    if (requestId && this.pendingRequests.has(requestId)) {
      devLog('[Messenger] handling pending request', {
        requestId,
        response,
        pendingRequests: Array.from(this.pendingRequests.keys()),
      });

      const pending = this.pendingRequests.get(requestId)!;
      this.pendingRequests.delete(requestId);

      if (error) {
        pending.reject(new Error(error));
      } else {
        pending.resolve(response);
      }
      return;
    }

    // Handle incoming request/message
    if (!method) {
      return;
    }

    const handler = this.messageHandlers.get(method);
    if (!handler) {
      throw new Error(`No handler found for message method: ${method}`);
    }

    try {
      devLog('[Messenger] handling message', {
        method,
        payload,
        requestId,
      });
      const result = handler(payload);

      // If this was a request expecting a response, send it back
      if (requestId) {
        if (result instanceof Promise) {
          result
            .then((handlerResponse) => {
              this.sendResponse(requestId, handlerResponse);
            })
            .catch((handlerError) => {
              devLog('[Messenger] handling error', {
                method,
                payload,
                requestId,
                error: handlerError,
              });
              this.sendError(requestId, handlerError.message);
            });
        } else {
          this.sendResponse(requestId, result);
        }
      }
    } catch (handlerError) {
      devLog('[Messenger] handling error', {
        method,
        payload,
        requestId,
        error: handlerError,
      });
      if (requestId) {
        this.sendError(requestId, (handlerError as Error).message);
      } else {
        throw handlerError;
      }
    }
  }

  private sendResponse(requestId: string, response: unknown): void {
    this.targetWindow.postMessage(
      {
        messengerType: MESSENGER_TYPE,
        requestId,
        response,
      },
      this.targetOrigin,
    );
  }

  private sendError(requestId: string, error: string): void {
    this.targetWindow.postMessage(
      {
        messengerType: MESSENGER_TYPE,
        requestId,
        error,
      },
      this.targetOrigin,
    );
  }

  /**
   * Register a handler for incoming messages of a specific method
   */
  on<K extends keyof TRegistry>(
    messageMethod: K,
    handler: MessageHandler<TRegistry[K]['payload'], TRegistry[K]['response']>,
  ): void {
    this.messageHandlers.set(
      messageMethod,
      handler as MessageHandler<unknown, unknown>,
    );
  }

  /**
   * Remove a message handler
   */
  off(messageMethod: keyof TRegistry): void {
    this.messageHandlers.delete(messageMethod);
  }

  /**
   * Send a message without expecting a response (fire-and-forget)
   */
  send<K extends keyof TRegistry>(
    messageMethod: K,
    payload: TRegistry[K]['payload'],
  ): void {
    devLog('[Messenger] sending message', {
      messageMethod,
      payload,
    });

    this.targetWindow.postMessage(
      {
        messengerType: MESSENGER_TYPE,
        method: messageMethod,
        payload,
      },
      this.targetOrigin,
    );
  }

  /**
   * Send a request and wait for a response
   */
  request<K extends keyof TRegistry>(
    messageMethod: K,
    payload: TRegistry[K]['payload'],
  ): Promise<TRegistry[K]['response']> {
    const requestId = generateRequestId(String(messageMethod));
    devLog('[Messenger] sending request', {
      messageMethod,
      payload,
      requestId,
    });

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
      });

      this.targetWindow.postMessage(
        {
          messengerType: MESSENGER_TYPE,
          method: messageMethod,
          payload,
          requestId,
        },
        this.targetOrigin,
      );
    });
  }

  /**
   * Clean up all pending requests and handlers
   */
  destroy(): void {
    devLog('[Messenger] destroying messenger');
    // Remove the event listener
    window.removeEventListener('message', this.boundMessageListener);

    // Clear all pending requests
    Array.from(this.pendingRequests.entries()).forEach(
      ([_requestId, pending]) => {
        pending.reject(new Error('Messenger destroyed'));
      },
    );
    this.pendingRequests.clear();
    this.messageHandlers.clear();
  }
}
